/**
 * PerksAndRewardsSection — loyalty perks banner in the account dropdown.
 *
 * Faithful behavioral port of composable
 * `hec/builder/blocks/account/perks-and-rewards-section/perksAndRewardsSection.jsx`.
 * Renders the four Annex Cloud states:
 *   loggedOut → notEnrolled → enrolledWithNoPoints → enrolledWithPoints
 *
 * WORKERS DEVIATION (approved — same functionality, more performant):
 * composable fetches enrollment/points/tier LIVE from Annex client-side
 * (react-query → Express `/api/annexcloud` proxy). Workers deliberately keeps
 * the Annex signing token server-only, so we derive the SAME values
 * synchronously from the persisted customer profile blob `c_acLoyaltyData` via
 * `parseLoyaltyData` — the exact pattern the cart Perks Banner already uses.
 * Fully SSR. Nothing here asks Annex: the block shows the saved copy.
 */
import modelMap from "hec/builder/model-map";
import { Button } from "hec/components/cta";
import { getDetermineRouteTypeAndUrl } from "hec/hooks/useDetermineRouteTypeAndUrl";
import type React from "react";
import { Suspense, useCallback } from "react";
import { Await, useRevalidator } from "react-router";

import { isEditing } from "@builder.io/sdk-react";
import type { RegisteredComponent } from "@builder.io/sdk-react";

import { useAuth } from "~/hec/context/auth-context";
import {
  isOptedIn,
  loyaltyPoints,
  parseLoyaltyData,
} from "~/hec/loyalty/loyalty-data.logic";
import { POINTS_UPDATING } from "~/hec/loyalty/perks-copy";
import { enrollInPerks } from "~/hec/utils/perks-bridge";
import { type SiteId } from "~/hec/utils/site-id";
import { useRootLayoutData } from "~/root";

import type { ModelReference } from "../BuilderReferenceContent";
import { buttonFunctions } from "../cta/cta.helper";
import useFetchPageDataForCta from "../cta/useFetchPageDataForCta";
import {
  type PerksUserState,
  derivePerksUserState,
  generateSubfields,
  perksAndRewardsStateMap,
} from "./utils";

interface QueryParam {
  name: string;
  value: string;
}

interface StateConfig {
  prompt?: string;
  // `ctaLink` is a Builder `page` reference (NOT a string) — see utils.ts.
  ctaLink?: ModelReference;
  ctaText?: string;
  ctaMode?: "drawer" | "link";
  queryParams?: QueryParam[];
}

interface PerksAndRewardsProps {
  perksAndRewardsData?: Partial<Record<PerksUserState, StateConfig>>;
  brandPerksImg?: string;
  /** Editor-only: which state to preview in the Builder canvas. */
  stateToShowInEditor?: PerksUserState;
  attributes?: Record<string, unknown>;
}

// Shared inline-link styling so every CTA matches the original loggedOut
// treatment that was already shipped (parity reference).
const CTA_CLASS = "font-semibold text-accent hover:underline";

/** Strip an authored trailing pipe (e.g. "…savings |") so we control the separator. */
const normalizePrompt = (prompt?: string) =>
  prompt?.replace(/\s*\|+\s*$/, "").trim();

/**
 * Resolve a `page` reference to its URL and render an in-app link (link mode).
 * Mirrors `cta/link.tsx`: the reference may be inlined by the SDK or fetched by
 * id, so resolve through Suspense/Await.
 */
const CtaPageLink: React.FC<{
  ctaLink?: ModelReference;
  ctaText: string;
  queryParams?: QueryParam[];
  dataTestId?: string;
}> = ({ ctaLink, ctaText, queryParams, dataTestId }) => {
  const { site } = useRootLayoutData();
  const { pageRef, isResolved } = useFetchPageDataForCta({
    isInternalLink: true,
    pageReference: ctaLink,
  });

  // ONE definition of the resolved link, shared by both paths (COM-198).
  const renderResolvedLink = (resolvedPageRef: unknown) => {
    const { routeType, url } = getDetermineRouteTypeAndUrl({
      clickFunction: buttonFunctions.linkToPage,
      isInternalLink: true,
      pageReference: resolvedPageRef as Record<string, unknown> | undefined,
      siteId: site.id as SiteId,
    });
    return (
      <Button
        ctaType="link"
        variant="normal"
        clickFunction={buttonFunctions.linkToPage}
        routeType={routeType ?? undefined}
        url={url ?? undefined}
        queryParams={queryParams}
        className={CTA_CLASS}
        dataTestId={dataTestId ?? "perksAndRewardsSection-Link"}
      >
        {ctaText}
      </Button>
    );
  };

  // Fast path: reference inlined server-side, so no Suspense boundary is opened.
  if (isResolved) {
    return renderResolvedLink(pageRef);
  }

  return (
    <Suspense
      fallback={
        <span className={CTA_CLASS} data-testid={dataTestId}>
          {ctaText}
        </span>
      }
    >
      <Await resolve={pageRef}>
        {resolvedPageRef => renderResolvedLink(resolvedPageRef)}
      </Await>
    </Suspense>
  );
};

/**
 * CTA for a state config. `drawer` mode never navigates — it opens the global
 * AccountDrawer via the `#login` hash (the drawer listens for it), mirroring
 * composable's `<a href="#login">` wrapper. Skips reference resolution entirely.
 */
const CtaLink: React.FC<{
  ctaLink?: ModelReference;
  ctaText: string;
  ctaMode?: "drawer" | "link";
  queryParams?: QueryParam[];
  dataTestId?: string;
}> = ({ ctaLink, ctaText, ctaMode, queryParams, dataTestId }) => {
  if (ctaMode === "drawer") {
    return (
      <a href="#login" className={CTA_CLASS} data-testid={dataTestId}>
        {ctaText}
      </a>
    );
  }
  return (
    <CtaPageLink
      ctaLink={ctaLink}
      ctaText={ctaText}
      queryParams={queryParams}
      dataTestId={dataTestId}
    />
  );
};

const PerksAndRewardsSection: React.FC<PerksAndRewardsProps> = ({
  perksAndRewardsData,
  brandPerksImg,
  stateToShowInEditor,
  attributes,
}) => {
  const { isLoggedIn, user } = useAuth();
  const { site } = useRootLayoutData();
  const revalidator = useRevalidator();
  const editing = isEditing();

  // Profile-sourced loyalty state (see file header). Null ⇒ not enrolled.
  const loyalty = isLoggedIn ? parseLoyaltyData(user, site.id) : null;
  const isEnrolled = isOptedIn(loyalty);
  // Undefined after a failed Annex read (`UNKNOWN_POINTS`): render the member
  // with "updating", never the no-points state a coerced 0 would pick.
  const livePoints = loyaltyPoints(loyalty);

  // In the Builder editor there is no real customer — drive the preview from
  // `stateToShowInEditor` and use representative sample points/tier so the
  // enrolled states render with content (composable parity via mockCustomerData).
  const pointsEarned = editing ? 1234 : livePoints;
  const currentTier = editing ? "Gold" : loyalty?.currentTier;

  const userState = derivePerksUserState({
    editing,
    stateToShowInEditor,
    isLoggedIn,
    isEnrolled,
    points: livePoints,
  });

  const contentData = perksAndRewardsData?.[userState];

  // Enroll → sync points → revalidate so the refreshed `c_acLoyaltyData`
  // re-derives the state (Workers equivalent of composable's query invalidation).
  const handleEnroll = useCallback(() => {
    void enrollInPerks(site.id).then(result => {
      if (result.ok) void revalidator.revalidate();
    });
  }, [site.id, revalidator]);

  // Tier line shared by both enrolled states: "<Tier> Tier | <CTA>".
  const renderTierInfo = () => {
    const { ctaLink, ctaText, queryParams } = contentData ?? {};
    return (
      <p>
        {currentTier && <span className="font-medium">{currentTier} Tier</span>}
        {ctaText && (
          <>
            {currentTier && " | "}
            <CtaLink
              ctaLink={ctaLink}
              ctaText={ctaText}
              queryParams={queryParams}
              dataTestId="perksAndRewardsSection-TierLink"
            />
          </>
        )}
      </p>
    );
  };

  const renderState = () => {
    const { prompt, ctaLink, ctaText, ctaMode, queryParams } =
      contentData ?? {};
    const promptText = normalizePrompt(prompt);

    switch (userState) {
      case perksAndRewardsStateMap.LOGGED_OUT:
        return (
          <p>
            {promptText && <span>{promptText}</span>}
            {ctaText && (
              <>
                {promptText && " | "}
                <CtaLink
                  ctaLink={ctaLink}
                  ctaText={ctaText}
                  ctaMode={ctaMode}
                  queryParams={queryParams}
                  dataTestId="perksAndRewardsSection-CTALink"
                />
              </>
            )}
          </p>
        );

      case perksAndRewardsStateMap.NOT_ENROLLED:
        return (
          <p>
            {promptText && <span>{promptText}</span>}
            {ctaText && (
              <>
                {promptText && " | "}
                <Button
                  ctaType="link"
                  variant="normal"
                  clickFunction={buttonFunctions.nothing}
                  onClick={handleEnroll}
                  className={CTA_CLASS}
                  dataTestId="perksAndRewardsSection-EnrollButton"
                >
                  {ctaText}
                </Button>
              </>
            )}
          </p>
        );

      case perksAndRewardsStateMap.ENROLLED_WITH_NO_POINTS:
        return (
          <>
            {promptText && <p className="mb-1">{promptText}</p>}
            {renderTierInfo()}
          </>
        );

      case perksAndRewardsStateMap.ENROLLED_WITH_POINTS:
        return (
          <>
            <p>
              {pointsEarned !== undefined ? (
                <>
                  <span className="font-medium">{pointsEarned}</span> Perks
                  Points
                </>
              ) : (
                <span
                  role="status"
                  data-testid="loyalty-perks-points-updating-message"
                >
                  {POINTS_UPDATING}
                </span>
              )}
            </p>
            {renderTierInfo()}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div
      {...attributes}
      className="flex max-w-full items-start gap-4 border-y border-popover-foreground px-4 py-4"
    >
      {brandPerksImg && (
        // Width-based sizing mirrors composable `perksAndRewardsSection.jsx`
        // (`w-2/5 contain`): image fits within 40% of parent without distortion.
        <img
          src={brandPerksImg}
          alt="Brand Perks Logo"
          className="w-2/5 min-w-0 object-contain"
          loading="lazy"
        />
      )}
      <div className="min-w-0 flex-1 text-right text-sm text-emphasis">
        {renderState()}
      </div>
    </div>
  );
};

export const PerksAndRewardsSectionDefinition = {
  component: PerksAndRewardsSection,
  name: "PerksAndRewardsSection",
  image: "https://unpkg.com/css.gg@2.0.0/icons/svg/shape-square.svg",
  noWrap: true,
  inputs: [
    {
      name: "perksAndRewardsData",
      type: "object",
      required: true,
      subFields: generateSubfields(),
    },
    {
      name: "brandPerksImg",
      type: "file",
      required: true,
      allowedFileTypes: ["jpeg", "jpg", "png", "svg", "webp"],
    },
    {
      name: "stateToShowInEditor",
      type: "string",
      enum: Object.values(perksAndRewardsStateMap),
      defaultValue: perksAndRewardsStateMap.LOGGED_OUT,
    },
    {
      // Parity with the shared Builder model: composable uses this to override
      // the login drawer's labels when the loggedOut CTA opens it. In Workers
      // the GLOBAL AccountDrawer (triggered via `#login`) renders the form and
      // carries its own `accountFormLabels`, so this input is accepted for
      // cross-platform content parity rather than consumed directly here.
      name: "accountFormLabels",
      type: "reference",
      model: modelMap.accountFormLabels,
      helperText:
        "Account form labels for the login drawer triggered when logged out.",
    },
  ],
} as RegisteredComponent;
